import classnames from 'classnames';
import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppSelector } from '../../../../hooks/react-redux';
import { useFieldHighlightColor } from '../../../../hooks/useFieldHighlightColor';
import { widthsByType } from '../../../admin/dataset-builder/preview/tablePreview/TableHeader';
import { IconButton } from '../../../shared/button/IconButton';
import styles from '../../../shared/datasets/SharedStyling.module.scss';
import { CaretSide, Filter } from '../../../shared/icons';
import { Position } from '../../../shared/modal/PositionModal';
import { CustomTooltip, InformationTooltip, OverflowTooltip } from '../../../shared/tooltip';
import { AgencyDatasetFieldType, DatasetFieldType, FieldHighlight, isFilterColumn, OpenFieldType, TableDatasetFieldType } from '../../store';
import { getSearchFieldSection } from '../store';
import { TableFilterModal } from './filters/TableFilterModal';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { InstanceFieldTableFilter, InstanceTableFilterType } from '../store/filterTypes';

const { fixedWhite } = styles;

interface TableHeaderProps {
    id: string;
    label: string;
    description: string;
    refLabel?: string
    showRef?: boolean;
    type: TableDatasetFieldType | AgencyDatasetFieldType;
    aliasLabel?: string;
    columnCollapsed: boolean;
    toggleCollapseColumn: (id: string) => void;
    highlight: FieldHighlight | null | undefined;
    filters: InstanceFieldTableFilter[];
    updateTableFilter: (fieldId: string, type: DatasetFieldType, value: InstanceTableFilterType) => void;
    removeTableFilter: (fieldId: string) => void;
    filterDropdownOptions: DropdownOption[];
}

export const TableHeader: React.FC<TableHeaderProps> = ({ label, description, id, refLabel, showRef, type, aliasLabel, columnCollapsed, toggleCollapseColumn, highlight, filters, updateTableFilter, filterDropdownOptions = [], removeTableFilter }) => {
    const [filterPosition, setFilterPosition] = useState<Position | null>(null);
    const searchFieldSection = useAppSelector(getSearchFieldSection);
    const cellWidth = useMemo(() => columnCollapsed ? '16px' : `${widthsByType[type]}px`, [columnCollapsed, type]);
    const columnLabel = useMemo(() => aliasLabel || label || 'Label...', [aliasLabel, label]);

    const showFilterOption = useMemo(() => isFilterColumn(type), [type]);
    const filter = useMemo(() => {
        const filterField = filters.find(({ fieldId }) => fieldId === id);
        if (!isUndefined(filterField)) {
            return filterField.filter;
        }
        return null;
    }, [id, filters]);

    const filterApplied = useMemo(() => !isNull(filter), [filter]);

    const openFilterModal = (x: number, y: number) => setFilterPosition({ x, y });
    const closeFilterModal = () => setFilterPosition(null);

    const toggleColumn = useCallback(() => toggleCollapseColumn(id), [toggleCollapseColumn, id]);

    const { color, backgroundColor } = useFieldHighlightColor(highlight, true);

    const isSearchedField = useMemo(() => !isNull(searchFieldSection) && searchFieldSection.type === OpenFieldType.FIELD && searchFieldSection.fieldId === id, [searchFieldSection, id]);

    const updateFilter = useCallback((filterValue: InstanceTableFilterType) => updateTableFilter(id, type, filterValue), [updateTableFilter, id, type]);
    const removeFilter = useCallback(() => removeTableFilter(id), [removeTableFilter, id]);

    const headerContent = useMemo(() => columnCollapsed ? (
        <div className={styles.cellHeader}>
            <CustomTooltip overlayText={columnLabel}>
                <div className={styles.headerIconWrapper}>
                    <IconButton icon={CaretSide} onClick={toggleColumn} color={fixedWhite} fontSize={14} />
                </div>
            </CustomTooltip>
        </div>
    ) : (
        <div className={styles.cellHeader}>
            <OverflowTooltip className={classnames(styles.headerLabel, { [styles.emptyHeaderLabel]: !label })} overlayText={columnLabel} testId={`table-header-${id}-label`} />
            <div className={styles.iconsWrapper}>
                {aliasLabel && <InformationTooltip label='L' content={label} labelColor={fixedWhite} />}
                {description && <InformationTooltip content={description} labelColor={fixedWhite} />}
                {refLabel && showRef && <InformationTooltip content={refLabel} label='?' labelColor={fixedWhite} />}
                {showFilterOption && <IconButton icon={Filter} onClick={e => openFilterModal(e.clientX, e.clientY)} color={fixedWhite} fontSize={20} testId={`table-header-${id}-filter`} showIndicator={filterApplied} />}
                <div className={styles.flippedIcon}><IconButton icon={CaretSide} onClick={toggleColumn} color={fixedWhite} fontSize={14} /></div>
            </div>
        </div>
    ), [columnCollapsed, columnLabel, aliasLabel, description, refLabel, showRef, toggleColumn, label, id, showFilterOption, filterApplied]);

    return (
        <>
            <div
                className={classnames(styles.tableHeaderWrapper, {
                    [styles.searchedFieldWrapper]: isSearchedField
                })}
                style={{ width: cellWidth, minWidth: cellWidth, color, backgroundColor }}
                id={`dataset-instance-field-${id}`}
            >
                {headerContent}
            </div>
            {!isNull(filterPosition) &&
                <TableFilterModal
                    position={filterPosition}
                    type={type}
                    closeModal={closeFilterModal}
                    testId={`table-header-${id}-filter`}
                    filter={filter}
                    updateFilter={updateFilter}
                    removeFilter={removeFilter}
                    filterDropdownOptions={filterDropdownOptions}
                />
            }
        </>
    );
};
